import { Injectable, Signal, computed, inject } from '@angular/core';
import { CustomClaims } from '@padspin/rbac';
import { CurrentClaimsService } from '@padspin/ui-auth';
import { ProfileClaimsWatcherService } from '@padspin/ui-profile';
import { Auth, user } from '@angular/fire/auth';
import { map, switchMap } from 'rxjs/operators';

/**
 * This service fetches the most up-to-date custom claims for the current user.
 *  The custom claims that you can find on
 * `(await getIdTokenResult()).claims` are only updated when the ID token is
 * refreshed, which can take up to an hour. The claims are _also_ stored in
 * the user's 'profile' collection in Firestore, which this service uses.
 * There is also a `ParsedTokenClaims` service in `@padspin/ui-auth` that
 * uses the current ID token directly.
 */
@Injectable({ providedIn: 'root' })
export class CurrentCustomClaimsService {
  private readonly currentClaimsService = inject(CurrentClaimsService);
  private readonly pcws = inject(ProfileClaimsWatcherService);

  private claims = user(inject(Auth)).pipe(
    switchMap(async (u) => u?.getIdTokenResult()),
    map((idTokenResult) => idTokenResult?.claims),
  );

  private readonly claimsSignal = computed(() => {
    const claims = this.pcws.customClaims();
    if (!claims) {
      return null;
    }
    const token = this.currentClaimsService.currentClaims();
    if (!token) {
      return null;
    }
    return claims;
  });

  currentCustomClaims(): Signal<CustomClaims | null> {
    return this.claimsSignal;
  }
}
